import clsx from "clsx";
import type { ComponentProps } from "react";

import { BrandIcon } from "@shared/ui/brand-icon";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { ContactLink } from "@shared/ui/contact-link";
import { SelectableBadge } from "@shared/ui/selectable-badge";
import { Body } from "@shared/ui/typography/body";
import { Heading } from "@shared/ui/typography/heading";
import { contactUtils } from "@shared/utils";

import type { PointCardData } from "../../model";

import s from "./content.module.scss";

export type ContentProps = ComponentProps<"div"> & {
  data: PointCardData;
  clampDescriptionOnMobile?: boolean;
};

export const Content = (props: ContentProps) => {
  const {
    clampDescriptionOnMobile,
    data: {
      title,
      uid,
      description,
      location,
      averageCheck,
      phone,
      workTimeList,
      email,
      filters,
      badges,
    },
  } = props;

  const hasLabels =
    (Array.isArray(filters) && filters.length > 0) ||
    (Array.isArray(badges) && badges.length > 0);

  return (
    <div className={s.root}>
      <div className={s.leftPath}>
        <div className={s.title}>
          {title != null && title !== "" && (
            <Heading level={5} tag="h2">
              {title}
            </Heading>
          )}
          {description != null && description !== "" && (
            <Body
              size="caption"
              className={clsx(
                clampDescriptionOnMobile && s.descriptionClampMobile,
              )}
            >
              {description}
            </Body>
          )}
        </div>
        {hasLabels && (
          <div className={s.labels}>
            {filters?.map((f) =>
              f?.uid != null ? (
                <SelectableBadge
                  key={`filter-${f.uid}`}
                  as="span"
                  kind="tag"
                  selected={false}
                >
                  {f?.label ?? ""}
                </SelectableBadge>
              ) : null,
            )}
            {badges?.map((b) =>
              b?.uid != null ? (
                <SelectableBadge
                  key={`badge-${b.uid}`}
                  as="span"
                  kind="tag"
                  selected={false}
                >
                  {b?.label ?? ""}
                </SelectableBadge>
              ) : null,
            )}
          </div>
        )}
      </div>
      <div
        className={clsx(s.rightPath, {
          // [s.rightPathEnd]: shouldAlignContactsToEnd,
        })}
      >
        {Boolean(location) && (
          <div className={s.location}>
            <BrandIcon
              name="location"
              fillColor="cadmium"
              className={s.locationIcon}
            />
            <Body
              size="uppercase-small"
              weight="semiBold"
              className={s.locationText}
            >
              {location}
            </Body>
          </div>
        )}
        <div className={s.contacts}>
          {averageCheck && (
            <Body size="uppercase-small" weight="semiBold">
              {averageCheck}
            </Body>
          )}
          {workTimeList && workTimeList?.length > 0 && (
            <ul>
              {workTimeList.map((item, idx) =>
                item?.timeSlot != null && item.timeSlot !== "" ? (
                  <li key={item?.id ?? item?.timeSlot ?? idx}>
                    <Body size="uppercase-small" weight="semiBold">
                      {item.timeSlot}
                    </Body>
                  </li>
                ) : null,
              )}
            </ul>
          )}
          {phone && (
            <ContactLink
              type="phone"
              value={phone}
              className="global-link"
              phoneFormat="brackets"
            >
              <Body size="uppercase-small" weight="semiBold">
                {contactUtils.formatPhoneForDisplay(phone, "brackets")}
              </Body>
            </ContactLink>
          )}
          {email && (
            <ContactLink type="email" value={email} className="global-link">
              <Body size="uppercase-small" weight="semiBold">
                {email}
              </Body>
            </ContactLink>
          )}
        </div>
      </div>
      {hasLabels && (
        <div className={clsx(s.labels, s.tabletLabels)}>
          {filters?.map((f) =>
            f?.uid != null ? (
              <SelectableBadge
                key={`tablet-filter-${f.uid}`}
                as="span"
                kind="tag"
                selected={false}
              >
                {f?.label ?? ""}
              </SelectableBadge>
            ) : null,
          )}
          {badges?.map((b) =>
            b?.uid != null ? (
              <SelectableBadge
                key={`tablet-badge-${b.uid}`}
                as="span"
                kind="tag"
                selected={false}
              >
                {b?.label ?? ""}
              </SelectableBadge>
            ) : null,
          )}
        </div>
      )}
      <ButtonWithIcon
        variant="primary-dark"
        iconName="sur"
        iconStrokeColor="white"
        iconFillColor="gold"
        className={s.moreBtn}
      >
        Подробнее
      </ButtonWithIcon>
    </div>
  );
};

Content.displayName = "Content";
